import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { MSpinner, MField } from "components/Shared";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import StarRatings from "react-star-ratings";
import { isSatisfactionSurveyAvailable, setSatisfactionSurvey } from "requests/satisfactionSurvey/supportLine";
import responseHandler from "responseHandler";
import { SupportLineSatisfactionSurveyUpdateRequest } from "interfaces/Mueller.Business.Models";
import { DATA_TYPE_MULTI_LINE_STRING } from "typeConstants/dataTypes";

interface Props {
  match: { params: { uId: string } },
  makeToast: (arg: string) => void,
}

let STATUS = {
  NONE: "NONE",
  FETCHING: "FETCHING",
  READY: "READY",
  RATED: "RATED",
  EXPIRED: "EXPIRED",
  ERROR: "ERROR",
};

const SupportLineSatisfactionSurvey = (props: Props) => {
  const mounted = useRef(true);
  const { match: { params }, makeToast } = props;  
  const [status, setStatus] = useState(STATUS.NONE);

  const COMMENTS_MAXLENGTH = 3500;

  const starRatedColorConst ="#E03D4C";
  const starHoverColorConst ="#CA2030";  

  const [surveyResponse, setSurveyResponse] = useState<SupportLineSatisfactionSurveyUpdateRequest>({
    /** WorkItemId of the surveyed case */
    uid: params.uId,
    supportSatisfactionScore: 0,
    supportUnderstoodAndWorkedToResolveScore: 0,
    postCallConfidenceScore: 0,
    issueFullyResolvedScore: 0,
    issueFullyResolvedComments: "",
    isThereAnythingSupportCouldHaveDoneBetter: "",
    additionaFeedbackOnCall: "",
  });

  useEffect(() => {
    if(status === STATUS.NONE)
      fetchStatus();

    return () => { mounted.current = false; };
  }, []);

  const fetchStatus = () => {      
    isSatisfactionSurveyAvailable(
      params.uId, 
      (data) => { data && data.canCompleteSatisfactionSurvey ? setStatus(STATUS.READY) : setStatus(STATUS.EXPIRED); },
      () => { setStatus(STATUS.ERROR); },
      mounted
    );    
    setStatus(STATUS.FETCHING);
  };

  const updateSurveyResponse = (key : string, val : any) => {
    setSurveyResponse((prevSurveyResponse) => ({
      ...prevSurveyResponse,
      [key]: val
    }));
  }
  
  const submitFeedback = () => {  
    let passesValidation = true;
    let responseToSubmit = {...surveyResponse};    
    responseToSubmit.uid = params.uId;
    Object.keys(responseToSubmit).forEach((key) => {
      if (responseToSubmit[key] === null) {
        passesValidation = false;
      }
    });

    if(!passesValidation){  
      makeToast("Please fill out each question before submitting.");
      return;
    }
    
    setSatisfactionSurvey(params.uId, responseToSubmit, () => { 
      setStatus(STATUS.RATED); 
    }, () => setStatus(STATUS.READY), mounted);    
    setStatus(STATUS.FETCHING);
  };

  return (
    <div className="satisfaction-survey-root">
      {(status === STATUS.NONE || status === STATUS.FETCHING) && <MSpinner />}
      {status === STATUS.READY &&
        <>        
          <h3>Your feedback is important to us!</h3>
          <div className="rating-tip">(1 - Not at all satisfied, 2 - Somewhat dissatisfied, 3 - Neither dissatisfied or satisfied, 4 - Somewhat satisfied, 5 - Extremely satisfied)</div>                 
          <h4>How would you rate the support you received today?</h4>   
          <StarRatings 
            rating={surveyResponse.supportSatisfactionScore}
            changeRating={(nextRating) => updateSurveyResponse('supportSatisfactionScore', nextRating)}
            starRatedColor={starRatedColorConst}
            starHoverColor={starHoverColorConst}  
          /> 
          <h4>The support representative clearly understood my issue and worked to resolve it:</h4> 
          <StarRatings 
            rating={surveyResponse.supportUnderstoodAndWorkedToResolveScore}
            changeRating={(nextRating) => updateSurveyResponse('supportUnderstoodAndWorkedToResolveScore', nextRating)}
            starRatedColor={starRatedColorConst}
            starHoverColor={starHoverColorConst}  
          />                    
          <h4>I left the call feeling more confident in my ability to do the job:</h4>  
          <StarRatings 
            rating={surveyResponse.postCallConfidenceScore}
            changeRating={(nextRating) => updateSurveyResponse('postCallConfidenceScore', nextRating)}
            starRatedColor={starRatedColorConst}
            starHoverColor={starHoverColorConst}
          />    
          <h4>The agent did everything they could to assist me on this call:</h4>  
          <div className="rating-tip-1">(1 - Strongly Disagree, 2 - Disagree, 3 - Neither Agree or Disagree, 4 - Agree, 5 - Strongly Agree)</div>                           
          <>
            <StarRatings 
              rating={surveyResponse.issueFullyResolvedScore}
              changeRating={(nextRating) => updateSurveyResponse('issueFullyResolvedScore', nextRating)}
              starRatedColor={starRatedColorConst}
              starHoverColor={starHoverColorConst}
            />            
            {{...surveyResponse}.issueFullyResolvedScore < 3 && {...surveyResponse}.issueFullyResolvedScore > 0 ? 
            <>
              <h4>Please provide more detail:</h4> 
              <MField 
                  key="issueResolvedComments"
                  dataType={DATA_TYPE_MULTI_LINE_STRING}
                  onChange= {(e, i) => updateSurveyResponse('issueFullyResolvedComments', i)}
                />
            </> : <div/>}
          </>       
          <h4>Is there anything the support rep could have done better? (optional):</h4>                              
          <TextField
            variant="standard"
            id="support-rep-comments"
            label=""
            inputProps={{ maxLength: COMMENTS_MAXLENGTH }}
            multiline
            rows={2}
            onChange={(e) => updateSurveyResponse('isThereAnythingSupportCouldHaveDoneBetter', e.target.value)}
          />
          <h4>Any other feedback you would like to share about this call? (optional):</h4>                              
          <TextField
            variant="standard"
            id="rating-comments"
            label=""
            inputProps={{ maxLength: COMMENTS_MAXLENGTH }}
            multiline
            rows={2}
            onChange={(e) => updateSurveyResponse('additionaFeedbackOnCall', e.target.value)}
          />          
          <h3>Thank you for your time!</h3>
          <Button onClick={submitFeedback}>Submit</Button>
        </>
      }
      {status === STATUS.RATED &&
        <div className="satisfaction-survey-complete">
          <h3>Thank you for your feedback!</h3>
          <a href="https://www2.muellerreports.com/contact-us/">
            <Button>Contact Us</Button>
          </a>
        </div>
      }
      {status === STATUS.EXPIRED &&
        <div className="satisfaction-survey-complete">
          <h3>This link has expired.  Please contact customer service with any additional feedback.</h3>
          <a href="https://www2.muellerreports.com/contact-us/">
            <Button>Contact Us</Button>
          </a>
        </div>
      }
      {status === STATUS.ERROR &&
        <div className="satisfaction-survey-complete">
          <h3>Oops!  You appear to have reached this page in error.</h3>
          <a href="https://www2.muellerreports.com/contact-us/">
            <Button>Contact Us</Button>
          </a>
        </div>
      }
    </div>  
  );
};

const mapDispatchToProps = dispatch => ({
  makeToast: text => {
    responseHandler.customToast(text, "error", dispatch);
  }
});

export default connect(null, mapDispatchToProps)(SupportLineSatisfactionSurvey);